import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiSearch, FiX, FiChevronRight, FiHash, FiFilter, FiUser, FiStar, FiArrowUp, FiCornerDownLeft } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import KeyboardShortcutBadge from '../shared/KeyboardShortcutBadge';
import { resolveDateTags } from '../../utils/dateCommandUtils';

// messageTypes reçu en prop depuis Messages.jsx pour éviter un double fetch useMessageSettings
const SearchCommandModal = ({ isOpen, onClose, messages, onSelectMessage, messageTypes = [] }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredResults, setFilteredResults] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    const resultsRef = useRef(null);
    const { user } = useAuth();

    // Helper function to strip HTML tags for cleaner search
    const stripHtml = (html) => {
        if (!html) return '';
        // Resolve dynamic date tags first so dates like "février" are searchable
        const resolved = resolveDateTags(html);
        const tmp = document.createElement('div');
        tmp.innerHTML = resolved;
        return tmp.textContent || tmp.innerText || '';
    };

    // Focus l'input quand le modal s'ouvre
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setSearchTerm('');
            setSelectedIndex(0);
        }
    }, [isOpen]);

    // Filtrer les messages en fonction de la recherche
    useEffect(() => {
        if (!searchTerm.trim()) {
            // Afficher les messages favoris par défaut
            const favoriteIds = user?.favoriteMessages || [];
            const favorites = messages
                .filter(m => favoriteIds.includes(m._id))
                .slice(0, 8);
            setFilteredResults(favorites);
            setSelectedIndex(0);
            return;
        }

        const term = searchTerm.toLowerCase();
        const results = messages.filter(msg => {
            const titleMatch = msg.title?.toLowerCase().includes(term);
            // Strip HTML from content before searching
            const contentText = stripHtml(msg.content);
            const contentMatch = contentText.toLowerCase().includes(term);
            const typeMatch = msg.type?.toLowerCase().includes(term);
            const roleMatch = msg.role?.toLowerCase().includes(term);
            const authorMatch = msg.createdByName?.toLowerCase().includes(term);
            
            return titleMatch || contentMatch || typeMatch || roleMatch || authorMatch;
        }).slice(0, 8); // Limiter à 8 résultats

        setFilteredResults(results);
        setSelectedIndex(0);
    }, [searchTerm, messages, user]);

    // Fonction pour sélectionner un message
    const handleSelectMessage = useCallback((message) => {
        onSelectMessage(message);
        onClose();
    }, [onSelectMessage, onClose]);

    // Gérer la navigation au clavier avec support Tab
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    e.preventDefault();
                    onClose();
                    break;
                case 'ArrowDown':
                    e.preventDefault();
                    setSelectedIndex(prev => 
                        prev < filteredResults.length - 1 ? prev + 1 : prev
                    );
                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
                    break;
                case 'Tab':
                    // Support Tab pour navigation entre résultats
                    if (filteredResults.length > 0) {
                        e.preventDefault();
                        if (e.shiftKey) {
                            // Shift+Tab : remonter
                            setSelectedIndex(prev => prev > 0 ? prev - 1 : filteredResults.length - 1);
                        } else {
                            // Tab : descendre
                            setSelectedIndex(prev => prev < filteredResults.length - 1 ? prev + 1 : 0);
                        }
                    }
                    break;
                case 'Enter':
                    e.preventDefault();
                    if (filteredResults[selectedIndex]) {
                        handleSelectMessage(filteredResults[selectedIndex]);
                    }
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, filteredResults, selectedIndex, onClose, handleSelectMessage]);

    // Scroller vers l'élément sélectionné
    useEffect(() => {
        if (resultsRef.current) {
            const selectedElement = resultsRef.current.children[selectedIndex];
            if (selectedElement) {
                selectedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth'
                });
            }
        }
    }, [selectedIndex]);

    const getTypeColor = (typeName) => {
        const type = messageTypes.find(t => t.name === typeName);
        return type?.color || '#6366f1';
    };

    const getPriorityLabel = (priority) => {
        const levels = {
            'p1': { label: 'P1', color: 'text-red-500' },
            'p2': { label: 'P2', color: 'text-orange-500' },
            'p3': { label: 'P3', color: 'text-yellow-500' },
            'p4': { label: 'P4', color: 'text-blue-500' }
        };
        return levels[priority] || null;
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9998] transition-opacity"
                onClick={onClose}
            />
            
            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh] px-4">
                <div 
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="search-modal-title"
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200 dark:border-gray-700"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header avec barre de recherche */}
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <FiSearch className="text-gray-400 text-xl flex-shrink-0" aria-hidden="true" />
                        <input
                            ref={inputRef}
                            type="text"
                            id="search-modal-title"
                            placeholder="Rechercher un message..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Rechercher dans les messages"
                            className="flex-1 bg-transparent border-none outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 text-lg"
                        />
                        <button
                            onClick={onClose}
                            aria-label="Fermer la recherche"
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        >
                            <FiX className="text-xl" aria-hidden="true" />
                        </button>
                    </div>

                    {/* Résultats */}
                    <div 
                        ref={resultsRef}
                        role="listbox"
                        aria-label="Résultats de recherche"
                        className="max-h-[60vh] overflow-y-auto"
                    >
                        {searchTerm.trim() === '' && filteredResults.length > 0 && (
                            <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700/50">
                                <FiStar className="inline mr-2" />
                                Favoris
                            </div>
                        )}
                        
                        {filteredResults.length === 0 ? (
                            <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                                {searchTerm.trim() === '' ? (
                                    <>
                                        <FiSearch className="inline-block text-4xl mb-3 opacity-50" />
                                        <p className="text-sm">Aucun message favori</p>
                                        <p className="text-xs mt-1">Commencez à taper pour rechercher</p>
                                    </>
                                ) : (
                                    <>
                                        <FiSearch className="inline-block text-4xl mb-3 opacity-50" />
                                        <p className="text-sm">Aucun résultat trouvé</p>
                                        <p className="text-xs mt-1">Essayez un autre terme de recherche</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="py-2">
                                {filteredResults.map((message, index) => {
                                    const priorityInfo = getPriorityLabel(message.priority);
                                    const typeColor = getTypeColor(message.type);
                                    
                                    return (
                                        <button
                                            key={message._id}
                                            role="option"
                                            aria-selected={index === selectedIndex}
                                            onClick={() => handleSelectMessage(message)}
                                            onMouseEnter={() => setSelectedIndex(index)}
                                            onFocus={() => setSelectedIndex(index)}
                                            tabIndex={-1}
                                            className={`w-full px-4 py-3 flex items-center gap-3 transition-colors focus:outline-none ${
                                                index === selectedIndex
                                                    ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-2 ring-indigo-500/50'
                                                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            }`}
                                        >
                                            {/* Type indicator */}
                                            <div 
                                                className="w-1 h-12 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: typeColor }}
                                            />
                                            
                                            {/* Content */}
                                            <div className="flex-1 text-left min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                                        {message.title}
                                                    </h3>
                                                    {message.favorite && (
                                                        <FiStar className="text-yellow-500 flex-shrink-0 text-sm" />
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                                    {message.type && (
                                                        <span className="flex items-center gap-1">
                                                            <FiHash className="text-[10px]" />
                                                            {message.type}
                                                        </span>
                                                    )}
                                                    {message.role && (
                                                        <span className="flex items-center gap-1">
                                                            <FiFilter className="text-[10px]" />
                                                            {message.role}
                                                        </span>
                                                    )}
                                                    {message.createdByName && (
                                                        <span className="flex items-center gap-1">
                                                            <FiUser className="text-[10px]" />
                                                            {message.createdByName}
                                                        </span>
                                                    )}
                                                    {priorityInfo && (
                                                        <span className={`font-semibold ${priorityInfo.color}`}>
                                                            {priorityInfo.label}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Arrow indicator */}
                                            <FiChevronRight className={`text-gray-400 flex-shrink-0 ${
                                                index === selectedIndex ? 'opacity-100' : 'opacity-0'
                                            }`} />
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer avec aide */}
                    <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="flex items-center gap-1.5">
                                    <KeyboardShortcutBadge shortcut="↑↓Tab" icon={FiArrowUp} size="sm" variant="ghost" />
                                    <span className="text-gray-600 dark:text-gray-300">Naviguer</span>
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <KeyboardShortcutBadge shortcut="Enter" icon={FiCornerDownLeft} size="sm" variant="ghost" />
                                    <span className="text-gray-600 dark:text-gray-300">Sélectionner</span>
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <KeyboardShortcutBadge shortcut="Esc" icon={FiX} size="sm" variant="ghost" />
                                    <span className="text-gray-600 dark:text-gray-300">Fermer</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SearchCommandModal;
